import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, useTheme } from '@mui/material'
import { ErrorTooltip, LanguageUtils } from '@pbt/pbt-ui-components'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import Typography from '~/components/elements/Typography/Typography'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'
import { getInventoryItemDisplayName } from '~/utils/inventoryItemUtils'

import { getPluralPackageLabel } from '../../inventoryUtils'

interface OrderItemReceiptsSummaryProps {
  inventoryOrderItem: InventoryOrderItem
  orderedQuantity: number
  totalQuantityDamaged: number
  totalQuantityReceived: number
}

export const OrderItemReceiptsSummary = ({
  inventoryOrderItem,
  totalQuantityReceived,
  totalQuantityDamaged,
  orderedQuantity,
}: OrderItemReceiptsSummaryProps) => {
  const { t } = useTranslation('Admin')
  const PackageType = useSelector(getPackageType)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const theme = useTheme()

  const productUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )
  const pluralPackageLabel = getPluralPackageLabel(
    inventoryOrderItem.packageType.id,
    PackageType,
  )

  const showTotalQuantityReceived = totalQuantityReceived > 0
  const showTotalQuantityDamaged = totalQuantityDamaged > 0
  const showSummary = showTotalQuantityReceived || showTotalQuantityDamaged

  if (!showSummary) {
    return null
  }

  const renderWithQuantityErrorTooltip = (
    showTooltip: boolean,
    children: React.ReactElement,
  ) => (
    <ErrorTooltip
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ],
      }}
      errorColor={theme.colors.uiNeedsAttention}
      message={t('Admin:CATALOG.ORDERS.GREATER_THAN_QUANTITY_ORDERED')}
      open={showTooltip}
      placement="right"
    >
      {children}
    </ErrorTooltip>
  )

  const isReceived = inventoryOrderItem.status.name.toUpperCase() === 'RECEIVED'
  const isReceivedQuantityOverage = totalQuantityReceived > orderedQuantity
  const isDamagedQuantityOverage = totalQuantityDamaged > orderedQuantity
  const showReceivedTooltip = isReceived && isReceivedQuantityOverage
  const showDamagedTooltip = isReceived && isDamagedQuantityOverage

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography.H3 color="grayGray1">
        {t('Admin:CATALOG.ORDERS.TOTAL_RECEIVING_QUANTITY_TITLE')}
      </Typography.H3>
      {showTotalQuantityReceived &&
        renderWithQuantityErrorTooltip(
          showReceivedTooltip,
          <Typography.Paragraph
            color={isReceivedQuantityOverage ? 'uiNeedsAttention' : 'grayGray1'}
            sx={{ width: 'fit-content' }}
          >
            {t('Admin:CATALOG.ORDERS.TOTAL_QUANTITY_RECEIVED_SUMMARY', {
              totalQuantityReceived,
              packageType: pluralPackageLabel,
              perPackageAmount: inventoryOrderItem.perPackageAmount,
              total:
                inventoryOrderItem.perPackageAmount * totalQuantityReceived,
              productUnit,
            })}
          </Typography.Paragraph>,
        )}
      {showTotalQuantityDamaged &&
        renderWithQuantityErrorTooltip(
          showDamagedTooltip,
          <Typography.Paragraph
            color={isDamagedQuantityOverage ? 'uiNeedsAttention' : 'grayGray1'}
            sx={{ width: 'fit-content' }}
          >
            {`(${totalQuantityDamaged} ${pluralPackageLabel} ${t(
              'Admin:CATALOG.ORDERS.ORDER_ITEM_DAMAGED_QUANTITY',
            )})`}
          </Typography.Paragraph>,
        )}
      <Typography.Paragraph color="grayGray1">
        {t('Admin:CATALOG.ORDERS.RECEIVING_AS_LABEL', {
          itemName: getInventoryItemDisplayName(
            inventoryOrderItem?.inventoryItem,
          ),
        })}
      </Typography.Paragraph>
    </Box>
  )
}
